import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { waLink } from "@/lib/api";

export function FaqSection() {
  const { faqs } = useSite();
  return (
    <section data-testid="faq-section" className="py-20 sm:py-28">
      <div className="container-site grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <Eyebrow>Common Questions</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-deep leading-[1.1]">
            Everything Farmers &amp; Partners Ask Us
          </h2>
          <p className="mt-4 text-brand-charcoal/65 leading-relaxed">
            Quick answers about our products, packaging, partnership process and how to buy ICROPSIA seeds.
          </p>
        </Reveal>
        <Reveal className="lg:col-span-3" delay={0.1}>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.id || i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border border-brand-deep/10 rounded-2xl px-6 bg-white data-[state=open]:border-brand-gold/50 data-[state=open]:shadow-lg data-[state=open]:shadow-brand-deep/5 transition-all"
              >
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-bold text-brand-deep text-base hover:no-underline py-5">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-brand-charcoal/65 leading-relaxed pb-5">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCta() {
  const { settings } = useSite();
  return (
    <section data-testid="final-cta-section" className="container-site pb-24">
      <Reveal>
        <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-brand-deep/25">
          <img
            src="https://images.unsplash.com/photo-1600284858934-d05fde407182?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Golden wheat field ready for harvest"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/90 via-brand-deep/75 to-brand-deep/40" />
          <div className="absolute inset-0 grain opacity-25" />
          <div className="relative px-7 py-20 sm:px-14 sm:py-24 max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.08]">
              Your Next Season Starts With the <span className="font-editorial italic font-semibold text-brand-wheat">Right Seed.</span>
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Explore the ICROPSIA portfolio or connect with our team for product, retail, and bulk supply enquiries.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/products"
                data-testid="final-cta-explore"
                className="inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-7 py-4 text-sm font-bold text-brand-deep transition-all hover:bg-brand-wheat"
              >
                Explore Products <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={waLink(settings, "Hello ICROPSIA team, I would like to connect regarding your seeds.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-whatsapp"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/35 px-7 py-4 text-sm font-bold text-white transition-all hover:bg-white hover:text-brand-deep"
              >
                <MessageCircle className="w-4 h-4" /> Talk to ICROPSIA
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
